import React from "react";

import { nullToEmpty } from "@impero-com/common/utils/string";
import { FormLabel } from "@impero-com/ui";

import { ButtonsBehaviour, ElementsAndSectionsBehaviour } from "./behaviours";
import { Section, SectionPlaceholder } from "./section";
import { SectionsContainer } from "./sectionsContainer";
import * as theme from "./theme.css";
import {
    SectionAccessors,
    InternalElementAccessors,
    ElementPosition,
    PlaceholderPositionAndRender,
    SectionPlaceholderPositionAndRender,
} from "./types";

/**
 * The content panel, contains a list of sections. Sections can be dragged and dropped.
 */
export interface BaseContentPanelProps<
    SEC,
    ELT,
    ElementButtonSpecId extends string,
>
    extends
        Omit<SectionAccessors<SEC, ELT>, "renderSectionPlaceholder">,
        InternalElementAccessors<ELT> {
    className?: string;
    title: string;
    tooltip?: string;
    sectionsClassName?: string;
    sectionSpecs: Array<SEC>;
    buttonsBehaviour: ButtonsBehaviour<ElementButtonSpecId>;
    elementsAndSectionsBehaviour: ElementsAndSectionsBehaviour;
}

interface ContentPanelProps<
    SEC,
    ELT,
    ElementButtonSpecId extends string,
> extends BaseContentPanelProps<SEC, ELT, ElementButtonSpecId> {
    draggedElementPosition: ElementPosition | undefined;
    eltPlaceholderPositionAndRender: PlaceholderPositionAndRender | undefined;
    sectionPlaceholderPosition:
        | SectionPlaceholderPositionAndRender<SEC>
        | undefined;
    children?: never;
    noElementsLabel?: string;
}

export function ContentPanel<SEC, ELT, ElementButtonSpecId extends string>({
    className,
    title,
    tooltip,
    sectionsClassName,
    sectionSpecs,
    getSectionId,
    getColCount,
    getElementColumns,
    getElementId,
    renderElement,
    buttonsBehaviour,
    elementsAndSectionsBehaviour,
    renderSection,
    sectionPlaceholderPosition,
    draggedElementPosition,
    eltPlaceholderPositionAndRender,
    noElementsLabel,
}: ContentPanelProps<SEC, ELT, ElementButtonSpecId>) {
    const sections = sectionSpecs.reduce(
        (acc: Array<React.ReactElement>, sectionSpec, index) => {
            // Don't render the section if it's being dragged
            if (
                sectionPlaceholderPosition &&
                index === sectionPlaceholderPosition.draggedSectionIndex
            ) {
                return acc;
            }
            const id = getSectionId(sectionSpec);
            acc.push(
                <Section<ELT, ElementButtonSpecId>
                    key={id}
                    id={id}
                    index={index}
                    getElementId={getElementId}
                    renderElement={renderElement}
                    draggedElementLocation={
                        eltPlaceholderPositionAndRender &&
                        draggedElementPosition &&
                        draggedElementPosition.sectionId === id
                            ? draggedElementPosition.location
                            : undefined
                    }
                    placeholderPositionAndRender={
                        eltPlaceholderPositionAndRender &&
                        eltPlaceholderPositionAndRender.position
                            .positionAfterDraggedElementIsRemoved.sectionId ===
                            id
                            ? eltPlaceholderPositionAndRender
                            : undefined
                    }
                    renderSection={(children) =>
                        renderSection(sectionSpec, children)
                    }
                    colCount={getColCount(sectionSpec)}
                    elementSpecColumns={getElementColumns(sectionSpec)}
                    buttonsBehaviour={buttonsBehaviour}
                    elementsAndSectionsBehaviour={elementsAndSectionsBehaviour}
                    noElementsLabel={noElementsLabel}
                />
            );
            return acc;
        },
        []
    );

    if (sectionPlaceholderPosition) {
        const { draggedSectionIndex } = sectionPlaceholderPosition;
        const draggedSectionSpec = sectionSpecs[draggedSectionIndex];
        const id = getSectionId(draggedSectionSpec);
        const { position } = sectionPlaceholderPosition;
        sections.splice(
            position.indexAfterDraggedSectionRemoved,
            0,
            <SectionPlaceholder<ELT>
                key={id}
                getElementId={getElementId}
                renderElement={renderElement}
                renderSectionPlaceholder={(children) =>
                    sectionPlaceholderPosition.render(
                        draggedSectionSpec,
                        children
                    )
                }
                colCount={getColCount(draggedSectionSpec)}
                elementSpecColumns={getElementColumns(draggedSectionSpec)}
            />
        );
    }
    return (
        <div className={`${theme.contentPanel} ${nullToEmpty(className)}`}>
            <FormLabel helpText={tooltip}>{title}</FormLabel>
            <SectionsContainer
                className={sectionsClassName}
                elementsAndSectionsBehaviour={elementsAndSectionsBehaviour}
            >
                {sections}
            </SectionsContainer>
        </div>
    );
}
